'use client';

export const dynamic = 'force-dynamic';

import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import api from '@/utils/api';
import {
	ShoppingCart,
	MapPin,
	Search,
	Store as StoreIcon,
	Package,
	Star,
	Share2,
	Info,
	Loader2,
	AlertCircle,
	CheckCircle2,
} from 'lucide-react';
import { useCart } from '@/contexts/CartContext';

// --- Types ---

type Store = {
	_id: string;
	name: string;
	description?: string;
	university?: string;
	logoUrl?: string | null;
	metadata?: Record<string, any>; // offerings, category, businessType
	createdAt?: string;
};

type Product = {
	_id: string;
	name: string;
	price: number;
	images?: { url?: string }[] | string[];
	shortDescription?: string;
	stock?: number;
	category?: any;
};

// --- Main Component ---

export default function StorePage() {
	// In Next.js 15 Client Components, we use useParams() instead of props.params
	const params = useParams();
	const slug = params?.slug as string;

	// --- State ---
	const [store, setStore] = useState<Store | null>(null);
	const [products, setProducts] = useState<Product[]>([]);
	const [loadingStore, setLoadingStore] = useState(true);
	const [loadingProducts, setLoadingProducts] =
		useState(true);
	const [error, setError] = useState<string | null>(null);
	const [q, setQ] = useState(''); // Local search state

	// --- Cart & Refs ---
	const { addItem } = useCart();
	const searchRef = useRef<number | null>(null);
	const [recentlyAdded, setRecentlyAdded] = useState<
		Record<string, boolean>
	>({});

	// --- Fetch Store Details ---
	useEffect(() => {
		if (!slug) return;
		setLoadingStore(true);

		const fetchStore = async () => {
			try {
				const res = await api.get(`/stores/${slug}`);
				const data =
					res.data?.store ??
					res.data?.data ??
					res.data ??
					null;
				setStore(data);
			} catch (err) {
				console.error('Failed to load store', err);
				setError('Store not found or failed to load.');
			} finally {
				setLoadingStore(false);
			}
		};

		fetchStore();
	}, [slug]);

	// --- Fetch Products (Triggered when store is loaded or search changes) ---
	useEffect(() => {
		if (!store?._id) return;

		const fetchProducts = async () => {
			setLoadingProducts(true);
			try {
				const res = await api.get('/products', {
					params: {
						storeId: store._id,
						limit: 100,
						q: q, // pass search query
					},
				});
				const data =
					res.data?.data ?? res.data?.products ?? [];
				setProducts(Array.isArray(data) ? data : []);
			} catch (err) {
				console.error(err);
			} finally {
				setLoadingProducts(false);
			}
		};

		// Debounce search
		if (searchRef.current) clearTimeout(searchRef.current);
		searchRef.current = window.setTimeout(() => {
			fetchProducts();
		}, 300);

		return () => {
			if (searchRef.current)
				clearTimeout(searchRef.current);
		};
	}, [store?._id, q]);

	// --- Handlers ---
	const handleAddToCart = (
		p: Product,
		e: React.MouseEvent,
	) => {
		e.preventDefault();
		e.stopPropagation();

		const imageUrl = Array.isArray(p.images)
			? (p.images[0] as any)?.url ?? p.images[0]
			: p.images;

		addItem(
			{
				id: p._id,
				name: p.name,
				price: p.price,
				image: typeof imageUrl === 'string' ? imageUrl : '',
				storeId: store?._id,
				storeName: store?.name,
			},
			1,
		);

		setRecentlyAdded((prev) => ({
			...prev,
			[p._id]: true,
		}));
		setTimeout(
			() =>
				setRecentlyAdded((prev) => {
					const copy = { ...prev };
					delete copy[p._id];
					return copy;
				}),
			1500,
		);
	};

	// --- Loading / Error States ---
	if (loadingStore) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
				<Loader2 className="w-10 h-10 animate-spin text-blue-900 mb-4" />
				<p className="text-gray-500 font-medium">
					Loading store...
				</p>
			</div>
		);
	}

	if (error || !store) {
		return (
			<div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4 text-center">
				<div className="bg-red-100 p-4 rounded-full mb-4">
					<StoreIcon className="w-10 h-10 text-red-600" />
				</div>
				<h1 className="text-2xl font-bold text-gray-900 mb-2">
					Store Not Found
				</h1>
				<p className="text-gray-600 mb-6">
					The store you are looking for does not exist or
					has been removed.
				</p>
				<Link
					href="/stores"
					className="px-6 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition"
				>
					Browse All Stores
				</Link>
			</div>
		);
	}

	// Extract offerings safely
	const offerings = (store.metadata?.offerings ||
		store.metadata?.offeringsList ||
		[]) as string[];

	return (
		<div className="min-h-screen bg-[#F8F9FA]">
			{/* --- Hero Banner --- */}
			<div className="bg-white border-b">
				{/* Gradient Header */}
				<div className="h-32 md:h-48 bg-gradient-to-r from-[#051449] to-blue-900 w-full relative">
					<div className="absolute top-4 right-4 flex gap-2">
						<button className="p-2 rounded-full text-white hover:bg-white/20 transition">
							{/* <Share2 className="w-5 h-5" /> */}
						</button>
					</div>
				</div>

				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
					<div className="flex flex-col md:flex-row items-start gap-6 -mt-12 md:-mt-16">
						{/* Store Logo */}
						<div className="relative w-32 h-32 md:w-40 md:h-40 bg-white rounded-2xl shadow-lg border-4 border-white overflow-hidden flex-shrink-0">
							{store.logoUrl ? (
								<Image
									src={store.logoUrl}
									alt={store.name}
									fill
									className="object-cover"
								/>
							) : (
								<div className="w-full h-full flex items-center justify-center bg-gray-100 text-[#051449] font-bold text-4xl">
									{store.name[0]}
								</div>
							)}
						</div>

						{/* Store Info */}
						<div className="flex-1 pt-2 md:pt-16 w-full">
							<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
								<div>
									<h1 className="text-3xl font-bold text-[#051449]">
										{store.name}
									</h1>
									<div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
										<MapPin className="w-4 h-4 text-gray-400" />
										<span>
											{store.university || 'Campus Store'}
										</span>
										{/* Alumni Badge */}
										{(store as any).owner?.isAlumni && (
											<span className="flex items-center gap-1 text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full text-xs border border-blue-100">
												<CheckCircle2 className="w-3 h-3 text-blue-600 fill-blue-100" />
												<span>Alumni Verified</span>
											</span>
										)}
										{store.metadata?.category && (
											<>
												<span className="w-1 h-1 bg-gray-300 rounded-full" />
												<span className="text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full text-xs uppercase tracking-wide">
													{store.metadata.category}
												</span>
											</>
										)}
									</div>
								</div>
								{/* Stats / Quick Info */}
								<div className="flex gap-4 md:text-right text-sm text-gray-500">
									<div>
										<div className="font-bold text-gray-900">
											{products.length}
										</div>
										<div>Products</div>
									</div>
									<div>
										<div className="font-bold text-gray-900">
											{new Date(
												store.createdAt || Date.now(),
											).getFullYear()}
										</div>
										<div>Joined</div>
									</div>
								</div>
							</div>

							{/* Description */}
							{store.description && (
								<p className="mt-4 text-gray-600 max-w-3xl leading-relaxed">
									{store.description}
								</p>
							)}

							{/* Offerings Tags */}
							{offerings.length > 0 && (
								<div className="mt-4 flex flex-wrap gap-2">
									{offerings.map((off, i) => (
										<span
											key={i}
											className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
										>
											<CheckCircle className="w-3 h-3 mr-1.5 text-green-600" />
											{off}
										</span>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* --- Main Content --- */}
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				<div className="flex flex-col md:flex-row gap-8">
					{/* Sidebar (About / Info) */}
					<div className="w-full md:w-72 flex-shrink-0 space-y-6">
						<div className="bg-white rounded-xl shadow-sm border p-6">
							<h3 className="font-bold text-[#051449] mb-4 flex items-center gap-2">
								<Info className="w-4 h-4" /> About Store
							</h3>
							<div className="space-y-4 text-sm text-gray-600">
								<div className="flex justify-between border-b pb-2">
									<span>Type</span>
									<span className="font-medium text-gray-900">
										{store.metadata?.businessType ||
											'General'}
									</span>
								</div>
								<div className="flex justify-between border-b pb-2">
									<span>Verified</span>
									<span className="font-medium text-green-600">
										Yes
									</span>
								</div>
								<div className="flex justify-between">
									<span>Since</span>
									<span className="font-medium text-gray-900">
										{new Date(
											store.createdAt || Date.now(),
										).toLocaleDateString()}
									</span>
								</div>
							</div>
						</div>
					</div>

					{/* Products Column */}
					<div className="flex-1">
						{/* Search Bar */}
						<div className="mb-6 flex items-center justify-between gap-4 bg-white p-4 rounded-xl border shadow-sm">
							<h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
								<Package className="w-5 h-5 text-gray-400" />{' '}
								Products
							</h2>
							<div className="relative flex-1 max-w-sm">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
								<input
									type="text"
									placeholder={`Search in ${store.name}...`}
									value={q}
									onChange={(e) => setQ(e.target.value)}
									className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-100 outline-none transition-all"
								/>
							</div>
						</div>

						{/* Product Grid */}
						{loadingProducts ? (
							<div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
								{Array.from({ length: 6 }).map((_, i) => (
									<div
										key={i}
										className="bg-white rounded-xl h-72 p-4 animate-pulse"
									>
										<div className="bg-gray-200 h-40 rounded-lg mb-4" />
										<div className="bg-gray-200 h-4 w-3/4 rounded mb-2" />
										<div className="bg-gray-200 h-4 w-1/2 rounded" />
									</div>
								))}
							</div>
						) : products.length === 0 ? (
							<div className="text-center py-20 bg-white rounded-xl border border-dashed">
								<Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
								<h3 className="text-lg font-medium text-gray-900">
									No products found
								</h3>
								<p className="text-gray-500 text-sm">
									{q
										? `No results for "${q}"`
										: "This store hasn't added any products yet."}
								</p>
							</div>
						) : (
							<div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
								{products.map((product) => (
									<StoreProductCard
										key={product._id}
										product={product}
										onAddToCart={(e) =>
											handleAddToCart(product, e)
										}
										isAdded={!!recentlyAdded[product._id]}
									/>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

// --- Helper: Small Icon for offerings ---
function CheckCircle({
	className,
}: {
	className?: string;
}) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			className={className}
			stroke="currentColor"
			strokeWidth="3"
		>
			<path
				d="M20 6L9 17l-5-5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

// --- Local Product Card Component ---
function StoreProductCard({
	product,
	onAddToCart,
	isAdded,
}: {
	product: Product;
	onAddToCart: (e: React.MouseEvent) => void;
	isAdded: boolean;
}) {
	const imageUrl = Array.isArray(product.images)
		? (product.images[0] as any)?.url ?? product.images[0]
		: product.images;

	const formatCurrency = (n: number) =>
		new Intl.NumberFormat('en-NG', {
			style: 'currency',
			currency: 'NGN',
			maximumFractionDigits: 0,
		}).format(n);

	return (
		<Link
			href={`/products/${product._id}`}
			className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full"
		>
			<div className="relative aspect-square bg-gray-100 overflow-hidden">
				{imageUrl ? (
					<Image
						src={
							typeof imageUrl === 'string' ? imageUrl : ''
						}
						alt={product.name}
						fill
						className="object-cover group-hover:scale-105 transition-transform duration-500"
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center text-gray-300">
						<Package className="w-10 h-10" />
					</div>
				)}
				{product.stock === 0 && (
					<div className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
						Out of Stock
					</div>
				)}
			</div>

			<div className="p-4 flex flex-col flex-1">
				<div className="flex-1">
					{product.category && (
						<div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mb-1">
							{product.category.name}
						</div>
					)}
					<h3
						className="font-semibold text-gray-900 line-clamp-2 text-sm group-hover:text-blue-900 transition-colors"
						title={product.name}
					>
						{product.name}
					</h3>
				</div>

				<div className="mt-4 flex items-center justify-between border-t pt-3 border-gray-50">
					<span className="text-lg font-bold text-[#051449]">
						{formatCurrency(product.price)}
					</span>
					<button
						onClick={onAddToCart}
						disabled={product.stock === 0}
						className={`p-2 rounded-lg transition-all shadow-sm ${
							isAdded
								? 'bg-green-500 text-white'
								: 'bg-[#FFCF23] text-[#051449] hover:bg-[#ffbf00] hover:scale-105 active:scale-95'
						} disabled:opacity-50`}
					>
						{isAdded ? (
							<Star className="w-4 h-4 fill-current" />
						) : (
							<ShoppingCart className="w-4 h-4" />
						)}
					</button>
				</div>
			</div>
		</Link>
	);
}
